// @ts-strict-ignore
import { useState } from "react";

import { useStoreCurrentSetupPageMutation } from "@shared/data/mutations/StoreCurrentSetupPageMutation/StoreCurrentSetupPage.mutation";
import { OAuthAuthorizationStep } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthAuthorizationStep/OAuthAuthorizationStep";
import { OAuthCertificationStep } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthCertificationStep/OAuthCertificationStep";
import { OAuthLandingStep } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthLandingStep/OAuthLandingStep";
import { OAuthOnboardingWrapper } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthOnboardingWrapper/OAuthOnboardingWrapper";
import { OAuthSignInStep } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthSignInStep/OAuthSignInStep";
import { OAuthSignUpStep } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthSignUpStep/OAuthSignUpStep";

import { OAuthExternalApplicationStep } from "./OAuthExternalApplicationStep/OAuthExternalApplicationStep";
import { OAuthStep } from "./oauthStep";
import { EXTERNAL_APPLICATION_STRING } from "./types";

interface Props {
  applicationLogo: string;
  applicationName: string;
  clientId: string;
  redirectUri: string;
  responseType: string;
  scopes: string;
  state: string;
  applicationAccessLevel: string;
  ssoErrors?: string;
  ssoOrigin: string;
}

type OauthStoreCurrentSetupPage = "student_identification" | "certificate" | "complete";

const CanadaOAuthOnboardingFlow = ({
  applicationLogo,
  applicationName,
  clientId,
  redirectUri,
  responseType,
  scopes,
  state,
  applicationAccessLevel,
  ssoErrors,
  ssoOrigin,
}: Props) => {
  const [ssoOriginState, setSsoOriginState] = useState(ssoOrigin);
  const [ssoErrorMessage, setSsoErrorMessage] = useState(ssoErrors);
  const isExternalApp = applicationAccessLevel === EXTERNAL_APPLICATION_STRING;
  const [currentStep, setCurrentStep] = useState<OAuthStep>(
    isExternalApp ? OAuthStep.EXTERNAL_APPLICATION : OAuthStep.LANDING_PAGE
  );
  const [updateCurrentSetupPage] = useStoreCurrentSetupPageMutation();
  const target = history.state?.target;

  const handleSignInComplete = () => {
    if (isExternalApp) {
      setCurrentStep(OAuthStep.EXTERNAL_APPLICATION);
    } else {
      setCurrentStep(OAuthStep.AUTHORIZATION);
    }
  };

  const handleOnboardingComplete = () => {
    if (isExternalApp) {
      setSsoOriginState(null);
      setCurrentStep(OAuthStep.EXTERNAL_APPLICATION);
    } else {
      setCurrentStep(OAuthStep.AUTHORIZATION);
    }
  };

  const updateStoreCurrentSetupPage = async (
    step: OauthStoreCurrentSetupPage,
    forProfit?: boolean
  ) => {
    await updateCurrentSetupPage({
      variables: {
        input: {
          attributes: {
            skipToStep: step,
            apiClientId: clientId,
            forProfit: forProfit || false,
          },
        },
      },
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case OAuthStep.SIGN_IN:
        return (
          <OAuthSignInStep
            onSignInComplete={handleSignInComplete}
            ssoErrors={ssoErrorMessage}
            setSsoErrorMessage={setSsoErrorMessage}
            onUpdateCurrentStep={setCurrentStep}
          />
        );
      case OAuthStep.SIGN_UP:
        return (
          <OAuthSignUpStep
            setSsoErrorMessage={setSsoErrorMessage}
            clientId={clientId}
            onUpdateCurrentStep={setCurrentStep}
            ssoErrors={ssoErrorMessage}
          />
        );
      case OAuthStep.EXTERNAL_APPLICATION:
        return (
          <OAuthExternalApplicationStep
            target={target}
            ssoOrigin={ssoOriginState}
            onUpdateCurrentStep={setCurrentStep}
            redirectUri={redirectUri}
          />
        );
      case OAuthStep.CERTIFICATION:
        return (
          <OAuthCertificationStep
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateCurrentStep={setCurrentStep}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.AUTHORIZATION:
        return (
          <OAuthAuthorizationStep
            clientId={clientId}
            redirectUri={redirectUri}
            responseType={responseType}
            scopes={scopes}
            state={state}
            applicationName={applicationName}
          />
        );
      case OAuthStep.LANDING_PAGE:
        return (
          <OAuthLandingStep
            target={target}
            ssoOrigin={ssoOriginState}
            onUpdateCurrentStep={setCurrentStep}
          />
        );
    }
  };

  return (
    <div data-testid="canada-oauth">
      <OAuthOnboardingWrapper
        onUpdateCurrentStep={setCurrentStep}
        currentStep={currentStep}
        applicationLogo={applicationLogo}
      >
        {renderCurrentStep()}
      </OAuthOnboardingWrapper>
    </div>
  );
};

export { CanadaOAuthOnboardingFlow };
